import { Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { environment } from '@environments/environment';
import { fromEvent, merge, Subscription, throttleTime } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class InactivityService implements OnDestroy {
    private ngZone = inject(NgZone);

    /** 1 hora de inactividad antes de cerrar sesión */
    private readonly INACTIVITY_TIMEOUT_MS = environment.idleTimeMsm;

    /** Frecuencia de verificación del temporizador (cada 1 minuto) */
    private readonly CHECK_INTERVAL_MS = 60000;

    /** Clave en localStorage para sincronizar actividad entre pestañas */
    private readonly LAST_ACTIVITY_KEY = 'lastActivityAt';

    /** Eventos que cuentan como "usuario activo" */
    private readonly ACTIVITY_EVENTS = [
        'mousemove',
        'mousedown',
        'keydown',
        'scroll',
        'touchstart',
        'click',
        'wheel',
    ];

    private checkIntervalTimer: ReturnType<typeof setInterval> | null = null;
    private activitySubscription?: Subscription;
    private storageListener?: (event: StorageEvent) => void;
    private onTimeoutCallback: (() => void) | null = null;
    private isTracking = false;

    /**
     * Inicia el monitoreo de inactividad.
     * @param onTimeout Callback que se ejecuta cuando el usuario estuvo inactivo
     *                  el tiempo configurado. Típicamente: cerrar sesión.
     */
    start(onTimeout: () => void): void {
        if (this.isTracking) {
            this.onTimeoutCallback = onTimeout;
            this.registerActivity();
            return;
        }

        this.isTracking = true;
        this.onTimeoutCallback = onTimeout;

        // Registrar listeners FUERA de la zona de Angular
        this.ngZone.runOutsideAngular(() => {
            const events$ = merge(
                ...this.ACTIVITY_EVENTS.map((event) =>
                    fromEvent(document, event, { passive: true })
                )
            );

            // Aumentamos el throttleTime a 5 segundos para optimizar rendimiento
            // No necesitamos registrar la actividad en localStorage cada milisegundo.
            this.activitySubscription = events$
                .pipe(throttleTime(5000))
                .subscribe(() => this.registerActivity());

            // Iniciamos la verificación por intervalos
            this.startIntervalCheck();
        });

        // Sincronización entre pestañas
        this.storageListener = (event: StorageEvent) => {
            if (event.key === this.LAST_ACTIVITY_KEY) {
                if (event.newValue === null) {
                    // Si otra pestaña elimina la clave (ej. cerró sesión manualmente), 
                    // detenemos el tracking aquí también.
                    this.stop();
                }
                // Si hay un nuevo valor, no hacemos nada extra. 
                // El `setInterval` leerá la nueva fecha automáticamente.
            }
        };
        window.addEventListener('storage', this.storageListener);

        // Arrancar el timer inmediatamente
        this.registerActivity();
    }

    /** Detiene el monitoreo. Llamar al cerrar sesión de forma manual o automática. */
    stop(): void {
        this.isTracking = false;
        this.onTimeoutCallback = null;

        if (this.checkIntervalTimer) {
            clearInterval(this.checkIntervalTimer);
            this.checkIntervalTimer = null;
        }

        this.activitySubscription?.unsubscribe();
        this.activitySubscription = undefined;

        if (this.storageListener) {
            window.removeEventListener('storage', this.storageListener);
            this.storageListener = undefined;
        }

        localStorage.removeItem(this.LAST_ACTIVITY_KEY);
    }

    private registerActivity(): void {
        // Publicamos timestamp para que otras pestañas lo lean
        localStorage.setItem(this.LAST_ACTIVITY_KEY, Date.now().toString());
    }

    private startIntervalCheck(): void {
        if (this.checkIntervalTimer) {
            clearInterval(this.checkIntervalTimer);
        }

        // Revisamos periódicamente la última actividad registrada
        this.checkIntervalTimer = setInterval(() => {
            const lastActivityStr = localStorage.getItem(this.LAST_ACTIVITY_KEY);

            if (lastActivityStr) {
                const lastActivity = parseInt(lastActivityStr, 10);
                const timeSinceLastActivity = Date.now() - lastActivity;

                if (timeSinceLastActivity >= this.INACTIVITY_TIMEOUT_MS) {
                    // Volvemos a la zona de Angular para ejecutar el callback de expiración
                    this.ngZone.run(() => {
                        this.onTimeoutCallback?.();
                        this.stop(); // Detenemos el servicio una vez que expiró
                    });
                }
            }
        }, this.CHECK_INTERVAL_MS);
    }

    ngOnDestroy(): void {
        this.stop();
    }
}